<template>
  <section class="section">
    <div class="container content">
      <h1 class="title">Privacy Policy</h1>
      <p>Updated December 1, 2024</p>
      <p>
        Porsche Auto Insurance and its affiliated entities (“PAI”, “we”, “us”)
        recognize the importance of your privacy, and we want to be clear about
        how we use information that you provide to us, or that we collect from
        you. By using or visiting any PAI website, application, product, or
        service (collectively, the “Services”), you agree that you understand
        and accept the terms of this policy.
      </p>

      <b
        >** Please note: You may need to scroll down through several pages to
        review the entire policy. **</b
      >

      <h2 class="subtitle">What Information Do We Collect?</h2>

      <p>
        a. Personally-Identifiable Information (“PII”) that you provide to us or
        that we source from third parties in order to provide the Services, such
        as your:
      </p>

      <ul>
        <li>Name</li>
        <li>Address and household information</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Driver's license number</li>
        <li>Credit or other financial history</li>
        <li>Insurance coverage and claims history</li>
        <li>Department of Motor Vehicle data</li>
        <li>Vehicle identification and license plate numbers</li>
        <li>
          Other information that directly identifies a specific individual or
          household
        </li>
      </ul>

      <p>
        b. Non-Personal Information such as:
      </p>

      <ul>
        <li>Odometer readings</li>
        <li>Photographs of your vehicle</li>
        <li>
          The geographic location, time and duration when you access the
          Services
        </li>
        <li>
          The type of computer equipment or devices you use to access the
          Services
        </li>
      </ul>

      <p>
        Some information may be collected via “cookies”, programs, or other
        means. Such information may be associated with a specific anonymous
        user, but is considered “Non-Personal Information” provided it is not
        directly associated with your PII.
      </p>

      <h2 class="subtitle">Call Monitoring and Recording</h2>
      <p>
        If you communicate with us by telephone, we may monitor or record the
        call in accordance with applicable law.
      </p>

      <h2 class="subtitle">
        What We Do With The Information We Collect
      </h2>

      <p>
        a. PII is used by us for the following purposes:
      </p>
      <ul>
        <li>To provide, support, and administer the Services.</li>
        <li>
          To provide information you have requested, For example:
          <ul>
            <li class="sub-list">
              If you request product information via email, we will use your
              email address to send you that information.
            </li>
            <li class="sub-list">
              If you request an insurance quote, we will use your address,
              vehicle information, financial information, insurance history,
              driver data, and other information you may provide to create a
              quote tailored for you.
            </li>
          </ul>
        </li>
        <li>
          To contact you for marketing or customer care purposes related to
          Services.
        </li>
      </ul>

      <p>
        b. PII we collect from you may be shared with a third party only in the
        following instances:
      </p>
      <ul>
        <li>
          You have given us permission to share the information with a third
          party for a particular purpose.
        </li>
        <li>
          While using the Services, you authorize us to connect you to a third
          party (for example, by clicking a link from our website). In such
          cases, we will advise you of the information that will be shared and
          ask you to confirm your choice to be connected.
        </li>
        <li>
          We provide data to Porsche Financial Services, Inc. (“PFS”), Porsche
          Cars North America, Inc. (“PCNA”) and their subsidiaries and
          affiliates to report program performance, to ensure appropriate
          customer care, for marketing purposes, and to monitor quality of the
          Services. If you purchase a Porsche Auto Insurance policy, you consent
          to such data sharing.
        </li>
        <li>
          The information is required to provide you with Services or other
          information you have requested. In such cases, the third party must
          agree to use the information for only the prescribed purpose, and to
          maintain the confidentiality of the information. For example, when you
          request an insurance quote, we obtain information about your credit
          (where permitted) or your driving history from a third-party data
          provider.
        </li>
        <li>
          We determine, in good faith, that we are legally required to provide
          the information.
        </li>
      </ul>
      <p>
        c. We may use Non-Personal Information for any purpose, including
        disclosure to third parties. Typically, this information is used to
        analyze the usage of the Services and to enhance the Services.
      </p>
      <p>
        d. The above uses exclude SMS (text messaging) originator opt-in data
        and consent; such information will not be shared with any third parties
        except as required by applicable law.
      </p>

      <h2 class="subtitle">How To Opt-Out</h2>
      <p>
        At any time, you may request that we restrict our sharing of your PII to
        specific purposes, a process often referred to as “opting out”. If you
        would like to opt out, please contact us by email at
        <a href="mailto:privacy@PorscheAutoInsurance.com"
          >privacy@PorscheAutoInsurance.com</a
        >.
      </p>
      <p>
        We will use our reasonable efforts to process opt-out requests promptly.
      </p>
      <p>
        Please note if you have used or are currently using certain Services,
        you will not be able to opt-out of all communication with us.
        Specifically, if you have a Porsche Auto Insurance policy, we will seek
        to accommodate your communication preferences (for example, if you
        prefer email versus telephone) whenever possible.
      </p>

      <h2 class="subtitle">Information From Minors</h2>
      <p>
        PAI does not knowingly collect PII from individuals under age 16. In the
        event we learn that we have collected any PII from someone under the age
        of 16, we will attempt to identify and delete that information from our
        systems.
      </p>

      <h2 class="subtitle">Information Storage and Security</h2>
      <p>
        Any information we collect may be stored and processed in the United
        States or any other country in which we or our affiliates, subsidiaries,
        contractors or agents maintain facilities. Any data shared with PFS
        and/or PCNA may be stored or processed in the United States or any other
        country in which PFS, PCNA or their affiliates, subsidiaries,
        contractors or agents maintain facilities. We implement reasonable
        technical, administrative, and physical safeguards to help us protect
        your PII from unauthorized access, use, and disclosure. We also request
        that our third-party service providers use commercially reasonable
        security measures to protect your PII from unauthorized access or use.
        However, no method of electronically transmitting or storing data is
        ever completely secure, and therefore, we cannot guarantee that your PII
        will never be accessed, used, or disclosed in a manner that is
        inconsistent with this Privacy Policy.
      </p>

      <h2 class="subtitle">International Usage</h2>
      <p>
        The Services are for users located exclusively in the United States of
        America, as the terms of this Privacy Policy may or may not comply with
        foreign law. By using the Services, you consent to the use and
        disclosure of your information in accordance with this Privacy Policy
        and applicable state and federal law.
      </p>

      <h2 class="subtitle">Updating or Removal of Your Information</h2>
      <p>
        If you would like the PII that you have provided to be updated,
        corrected, or removed from our systems, please contact us via email at
        <a href="mailto:privacy@porscheautoinsurance.com"
          >privacy@PorscheAutoInsurance.com</a
        >. Please note in some cases, we may be required to retain your PII for
        a period of time pursuant to federal and state laws or regulations, even
        if you are no longer using the Services. For example, state insurance
        regulations often require policy records, including PII, to be kept for
        a certain number of years, even after the insurance policy has lapsed.
      </p>

      <h2 class="subtitle">Changes to this Policy</h2>
      <p>
        We reserve the right to change the terms of this Privacy Policy at any
        time. When we make changes, we will revise the “Updated” date at the top
        of the policy. Please periodically review this Privacy Policy for
        updates.
      </p>

      <h2 class="subtitle">Other Questions About This Privacy Policy</h2>
      <p>
        If you have any questions or concerns about this Privacy Policy, please
        contact us via email at
        <a href="mailto:privacy@PorscheAutoInsurance.com"
          >privacy@PorscheAutoInsurance.com</a
        >.
      </p>

      <h1 class="title">STATE SPECIFIC NOTICES</h1>

      <h2 class="subtitle">Notice to California Residents</h2>
      <p>
        In addition to our full Privacy Policy at
        <a href="/legal/privacy-policy"
          >https://www.porscheautoinsurance.com/legal/privacy-policy</a
        >, this CCPA Privacy Notice applies to certain California residents
        (“Consumers”) from whom we collect personal information as a business
        under the California Consumer Privacy Act of 2018 (California Civil Code
        §§ 1798.100 to 1798.199) and its implementing regulations, as amended or
        superseded from time to time (“CCPA”).
      </p>
      <p>
        For the purposes of this CCPA Privacy Notice, “Personal Information”
        means information that identifies, relates to, describes, is reasonably
        capable of being associated with, or could reasonably be linked,
        directly or indirectly, with a particular consumer or household, or as
        otherwise defined by the CCPA.
      </p>
      <p>
        Personal Information does not include information that is:
      </p>
      <ul>
        <li>Lawfully made available from government records;</li>
        <li>Deidentified or aggregate information; or</li>
        <li>Otherwise excluded from the scope of the CCPA.</li>
      </ul>
      <p>
        Consumer does not include a natural person acting as a job applicant to,
        employee of, owner of, director of, officer of, medical staff member of,
        or contractor of PAI to the extent such person’s Personal Information is
        collected within the context of the person acting in such role. Persons
        acting in those roles should review the applicable CCPA Limited Privacy
        Notice made available by PAI.
      </p>

      <h3 class="section-title italic">
        California Consumers’ Rights and Choices
      </h3>
      <p>
        If you are a Consumer, the CCPA may permit you to request information
        regarding the:
      </p>

      <ul>
        <li>
          Categories of Personal Information (as defined by the CCPA) collected,
          sold or disclosed by us;
        </li>
        <li>
          Purposes for which categories of Personal Information collected by us
          are used;
        </li>
        <li>
          Categories of sources from which we collect Personal Information;
        </li>
        <li>
          Categories of third parties with whom we share Personal Information;
          and
        </li>
        <li>
          Specific pieces of Personal Information we have collected about you
          and in some cases your Household.
        </li>
      </ul>

      <p>In addition, if you are a Consumer, you may:</p>

      <ul>
        <li>
          Opt out of the sale of your Personal Information, in some
          circumstances;
        </li>
        <li>
          Opt out of receiving marketing communications from us; however, you
          may still receive administrative communications regarding our
          services;
        </li>
        <li>
          Opt in to certain financial incentive programs we may offer related to
          the collection, retention, or sale of your Personal Information; and
        </li>
        <li>
          Request that we and our service providers delete Personal Information
          we have collected from you, in some circumstances.
        </li>
      </ul>

      <p>
        Further, the CCPA provides Consumers with the right not to receive
        discriminatory treatment by a business for the exercise of these CCPA
        privacy rights, subject to certain limitations.
      </p>

      <h3 class="section-title underline">
        Collection and Disclosure of Personal Information from Consumers
      </h3>
      <p>
        The table(s) below outline how we handle Personal Information collected
        about Consumers in the preceding 12 months from the date this CCPA
        Privacy Notice was last reviewed.
      </p>

      <table class="c18">
        <thead>
          <tr>
            <td colspan="2" rowspan="1">
              Category of personal information we may collect: Identifiers
            </td>
          </tr>
        </thead>
        <tbody>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              The personal information contains:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              The Identifiers may contain the following information: names,
              phone numbers, physical addresses, email addresses.
            </td>
          </tr>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              Whether we have sold or otherwise disclosed the Personal
              information in the previous 12 months:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We have not sold the Personal Information in the previous 12
              months. However, we may have otherwise shared the Personal
              Information with third parties in the previous 12 months.
            </td>
          </tr>
          <tr class="c26">
            <td class="left" colspan="1" rowspan="1">
              Categories of Third Parties to whom the Personal Information was
              disclosed:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We may have shared this Personal Information with the following
              categories of third parties: our service providers, data analytics
              providers, affiliates, third parties integrated into our services,
              third parties as required by law and similar disclosures.
            </td>
          </tr>
        </tbody>
      </table>

      <table class="c18">
        <thead>
          <tr class="c20">
            <td class="c21 c19" colspan="2" rowspan="1">
              Category of personal information we may collect: Personal
              Characteristics
            </td>
          </tr>
        </thead>
        <tbody>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              The personal information contains:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              The Personal Characteristics may contain the following
              information: gender, birth date, marital status.
            </td>
          </tr>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              Whether we have sold or otherwise disclosed the Personal
              information in the previous 12 months:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We have not sold the Personal Information in the previous 12
              months. However, we may have otherwise shared the Personal
              Information with third parties in the previous 12 months.
            </td>
          </tr>
          <tr class="c26">
            <td class="left" colspan="1" rowspan="1">
              Categories of Third Parties to whom the Personal Information was
              disclosed:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We may have shared this Personal Information with the following
              categories of third parties: our service providers, data analytics
              providers, affiliates, third parties integrated into our services,
              third parties as required by law and similar disclosures.
            </td>
          </tr>
        </tbody>
      </table>

      <table class="c18">
        <thead>
          <tr class="c20">
            <td class="c21 c19" colspan="2" rowspan="1">
              Category of personal information we may collect: Commercial
              Information
            </td>
          </tr>
        </thead>
        <tbody>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              The personal information contains:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              The Commercial Information may contain the following information:
              motor vehicle records.
            </td>
          </tr>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              Whether we have sold or otherwise disclosed the Personal
              information in the previous 12 months:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We have not sold the Personal Information in the previous 12
              months. However, we may have otherwise shared the Personal
              Information with third parties in the previous 12 months.
            </td>
          </tr>
          <tr class="c26">
            <td class="left" colspan="1" rowspan="1">
              Categories of Third Parties to whom the Personal Information was
              disclosed:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We may have shared this Personal Information with the following
              categories of third parties: our service providers, data analytics
              providers, affiliates, third parties integrated into our services,
              third parties as required by law and similar disclosures.
            </td>
          </tr>
        </tbody>
      </table>

      <table class="c18">
        <thead>
          <tr class="c20">
            <td class="c21 c19" colspan="2" rowspan="1">
              Category of personal information we may collect:
              Internet/Electronic Activity
            </td>
          </tr>
        </thead>
        <tbody>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              The personal information contains:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              The Internet/Electronic Activity may contain the following
              information: app use (ours), website use (ours), device type.
            </td>
          </tr>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              Whether we have sold or otherwise disclosed the Personal
              information in the previous 12 months:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We have not sold the Personal Information in the previous 12
              months. However, we may have otherwise shared the Personal
              Information with third parties in the previous 12 months.
            </td>
          </tr>
          <tr class="c26">
            <td class="left" colspan="1" rowspan="1">
              Categories of Third Parties to whom the Personal Information was
              disclosed:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We may have shared this Personal Information with the following
              categories of third parties: our service providers, data analytics
              providers, third parties integrated into our services, third
              parties as required by law and similar disclosures.
            </td>
          </tr>
        </tbody>
      </table>

      <table class="c18">
        <thead>
          <tr class="c20">
            <td class="c21 c19" colspan="2" rowspan="1">
              Category of personal information we may collect: Inferences
            </td>
          </tr>
        </thead>
        <tbody>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              The personal information contains:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              The Inferences may contain the following information: household
              and family members.
            </td>
          </tr>
          <tr class="c13">
            <td class="left" colspan="1" rowspan="1">
              Whether we have sold or otherwise disclosed the Personal
              information in the previous 12 months:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We have not sold the Personal Information in the previous 12
              months. However, we may have otherwise shared the Personal
              Information with third parties in the previous 12 months.
            </td>
          </tr>
          <tr class="c26">
            <td class="left" colspan="1" rowspan="1">
              Categories of Third Parties to whom the Personal Information was
              disclosed:
            </td>
            <td class="c7" colspan="1" rowspan="1">
              We may have shared this Personal Information with the following
              categories of third parties: our service providers, third parties
              integrated into our services, third parties as required by law and
              similar disclosures.
            </td>
          </tr>
        </tbody>
      </table>

      <h3 class="section-title italic">Use of Personal Information</h3>

      <p>
        The Personal Information may be used for the purposes disclosed to
        Consumers at the time of collection, in our general Privacy Policy (see
        https://www.porscheautoinsurance.com/legal/privacy-policy), and for the
        purposes of: providing our services, communicating with Consumers,
        facilitating the connection of third-party services or applications,
        verifying Consumers’ identities, developing and providing marketing
        materials, personalizing the experience of Consumers on our services
        (including providing tailored content), facilitating transactions and
        payments, deidentifying and aggregating the Personal Information,
        complying with judicial process or government requests, auditing related
        to a current interactions with Consumers and concurrent transactions
        (including but not limited to counting ad impressions to unique
        visitors, verifying positioning and quality of ad impressions, and
        auditing compliance with this specification and other standards),
        detecting security incidents (including protecting against malicious,
        deceptive, fraudulent, or illegal activity) and prosecuting those
        responsible, debugging to identify and repair errors that impair
        existing intended functionality, short-term transient use, performing
        services on our behalf or on behalf of our service providers (including
        maintaining or servicing accounts, providing customer service,
        processing or fulfilling orders and transactions, verifying customer
        information, processing payments, providing financing, providing
        advertising or marketing services, providing analytic services, or
        providing similar services for our or our service providers’ short-term
        transient use) and undertaking activities to verify or maintain the
        quality or safety of a service or device that is owned/manufactured for
        or controlled by us and to improve/upgrade/enhance the service or device
        that is owned/manufactured for or controlled by us. The Personal
        Information may also be used or disclosed as otherwise permitted or
        required by applicable law.
      </p>

      <h3 class="section-title italic">SMS Information</h3>
      <p>
        The above uses exclude SMS (text messaging) originator opt-in data and
        consent; such information will not be shared with any third parties
        except as required by applicable law.
      </p>

      <h3 class="section-title italic">Collection of Personal Information</h3>

      <p>
        In the preceding 12 months since this CCPA Privacy Notice was last
        reviewed, we have collected Personal Information from the following
        categories of sources: directly from you (e.g. forms you complete or
        products and services you purchase), indirectly from you (e.g. from
        observing your actions on our website), our affiliates, advertising
        networks, our business partners, and third party information providers.
      </p>

      <h3 class="section-title italic">Sale of Your Information</h3>
      <p>
        We have not sold your Personal Information for the preceding 12 months
        from the date of the last review of this CCPA Privacy Notice.
      </p>

      <h3 class="section-title italic">Privacy Requests</h3>
      <p>
        If you are a Consumer, you can make requests to know what personal
        information we have collected from you in the past 12 months or requests
        to delete your personal information via email at
        <a href="mailto:privacy@porscheautoinsurance.com">
          privacy@PorscheAutoInsurance.com </a
        >.
      </p>
      <p>
        Please be aware that we do not accept or process requests through other
        means (e.g., via fax or social media).
      </p>
      <p>
        We will review the information provided and may request additional
        information to ensure we are interacting with the correct Consumer. If
        you have a password-protected online account with us, you may be
        required to log-in to your account for identity verification. If you do
        not have an account with us, additional information to verify your
        identity may be required by law before we may take action upon such a
        request. This additional information may vary depending on the nature of
        your request and/or the nature of the information about which your
        request relates. In some cases, we may also be required by law to obtain
        a signed declaration under penalty of perjury from you that you are the
        subject of the request being made. If we suspect fraudulent or malicious
        activity on or from your account, we will delay taking action on your
        request until we can appropriately verify your identity and the request
        as authentic.
      </p>
      <p>
        After submitting your request online, you will receive a follow-up email
        outlining the estimated amount of time it will take to complete your
        request and giving further instructions. That email may also include a
        link you must click on in order to verify your identity. If you do not
        click on that link, we will be unable to complete your request due to
        lack of verification. It is important that you provide a valid email
        address in order for us to be able to process your request.
      </p>
      <p>
        By law, we are not required to collect Personal Information that we
        otherwise would not collect in the ordinary course of our business,
        retain Personal Information for longer than we would otherwise retain
        such information in the ordinary course of our business, or reidentify
        or otherwise link information that is not maintained in a manner that
        would be considered Personal Information. If we have not requested
        specific additional information from you to verify your request, please
        do not send such information.
      </p>
      <p>
        We generally will aim to avoid requesting additional information from
        you for the purposes of verification. However, if we cannot reasonably
        verify your identity or more information is needed for security or
        fraud-prevention purposes, we may consider any of the following factors,
        alone or in combination, in requesting additional information:
      </p>
      <ul>
        <li>
          The type, sensitivity, and value of the Personal Information collected
          and maintained about the Consumer, as applicable law requires a more
          stringent verification process for sensitive or valuable personal
          information;
        </li>
        <li>
          The risk of harm to the Consumer posed by any unauthorized access or
          deletion;
        </li>
        <li>
          The likelihood that fraudulent or malicious actors would seek the
          Personal Information;
        </li>
        <li>
          Whether the Personal Information to be provided by the Consumer to
          verify their identity is sufficiently robust to protect against
          fraudulent requests or being spoofed or fabricated;
        </li>
        <li>The manner in which we interact with the Consumer;</li>
        <li>Available technology for verification; and</li>
        <li>
          Other factors that may be reasonable in the circumstances, are
          consistent with industry practice, are recommended by California
          government officials, or which may be required by law or regulation
          following the effective date of this CCPA Privacy Notice.
        </li>
      </ul>
      <p>
        If your request is regarding household information, the same
        verification steps above may be required before we can provide you with
        aggregate household information. In addition, if you do not have a
        password-protected online account with us, for us to process a request
        for access to or deletion of specific pieces of information regarding
        your household, we may need all members of the household to make the
        request, and we may need to individually verify each household member
        and that each member making the request is currently a member of the
        house.
      </p>
      <p>
        We reserve the right to charge a reasonable fee or take other
        appropriate action in response to requests from a Consumer or household
        that are manifestly unfounded or excessive, in particular because of
        their repetitive character.
      </p>
      <h3 class="section-title italic">Authorized Agent Requests</h3>
      <p>
        You may designate an authorized agent to make a request on your behalf
        pursuant to applicable law. We accept documentation of your designation
        in the form of a valid power of attorney and/or a written authorization
        signed by you and the authorized agent. You may submit evidence of your
        designation of an authorized agent in writing to: PO Box 725369,
        Atlanta, GA 31139, USA or via email at
        <a href="mailto:privacy@porscheautoinsurance.com">
          privacy@PorscheAutoInsurance.com </a
        >.
      </p>
      <p>
        We may require verification of your authorized agent in addition to the
        information for verification above for Consumers and households.
      </p>
      <h3 class="section-title italic">Children</h3>
      <p>
        Our services are not directed to children, and we do not knowingly
        collect or sell Personal Information from children under the age of 16.
        If you learn that a child has provided us with Personal Information,
        then you may contact us through the Contact Us section below.
      </p>
      <h3 class="section-title italic">Notice of Financial Incentive</h3>
      <p>
        We do not offer financial incentives to Consumers based upon the
        collection, retention or sale of a Consumer’s Personal Information.
      </p>
      <h3 class="section-title italic">Contact Us</h3>
      <p>
        If you have any questions or concerns regarding this CCPA Privacy
        Notice, contact
        <a href="mailto:privacy@porscheautoinsurance.com">
          privacy@PorscheAutoInsurance.com </a
        >.
      </p>

      <h2 class="subtitle">Notice to Vermont Residents</h2>
      <p>
        In response to Vermont regulations, we automatically treat accounts with
        Vermont billing addresses as if you requested that we not share your
        information with non-affiliated third parties, with the exception of
        Porsche Financial Services, Inc. and its affiliates as described above,
        and that we limit the information we share with our affiliates.
      </p>

      <h2 class="subtitle">Notice to Nevada Residents</h2>
      <p>
        We are providing you this notice pursuant to state law. You may be
        placed on our internal “Do Not Call List” by requesting that we cease
        calling you by contacting us directly and making such request in writing
        via email at
        <a href="mailto:privacy@porscheautoinsurance.com">
          privacy@PorscheAutoInsurance.com </a
        >, or by calling us at
        <span style="white-space: nowrap"
          ><a style="text-decoration:underline;" href="tel:1-800-400-4128"
            >800-400-4128</a
          ></span
        >. Nevada law requires that we also provide you with the following
        contact information: Bureau of Consumer Protection, Office of the Nevada
        Attorney General, 555 E. Washington St., Suite 3900, Las Vegas, NV
        89101; Phone number: 702-486-3132; e-mail: BCPINFO@ag.state.nv.us.
      </p>

      <h2 class="subtitle">
        Notice to Residents of AZ, CT, GA, IL, ME, MN, MT, NV, NJ, NC, OR and VA
      </h2>
      <p>
        If you are a resident of Arizona, California, Connecticut, Georgia,
        Illinois, Maine, Minnesota, Montana, Nevada, New Jersey, North Carolina,
        Oregon, or Virginia, you have the right to access and correct the
        personal information that we have collected from or about you. To do so,
        simply contact us via email at
        <a href="privacy@PorscheAutoInsurance.com"
          >privacy@PorscheAutoInsurance.com</a
        >. If you'd like to correct information that you provided to us, we will
        make the appropriate adjustments to our records. If you wish to correct
        personal information provided to us by a third party (such as a credit
        rating agency) we will provide you with the applicable third party's
        contact information. Upon the request of residents of other states, we
        will take reasonable steps to verify the accuracy of the personal
        information in our records.
      </p>
    </div>
  </section>
</template>

<script>
export default {
  name: 'PrivacyPolicy'
};
</script>
<style lang="scss" scoped>
.bold,
.section-title {
  font-weight: bold;
}
.italic {
  font-style: italic;
}
.underline {
  text-decoration: underline;
}
.sub-text {
  font-size: 0.5rem;
}
table {
  border: solid 1px #000000;

  thead {
    background-color: #000000;
    td {
      text-align: center;
      font-weight: bold;
      color: $white;
    }
  }
  td {
    border: solid 1px #000000;
  }
  td.left {
    font-weight: bold;
    background-color: $grey-light;
    color: $black;
  }
}
</style>
